import { THEME_A_CSS_VARIABLES } from "./tokens";

const freeze = (value) => Object.freeze(value);

// Thèmes du design system V2 (charte couleurs 2026-09-01).
//
// Un thème est un jeu de surcharges de variables CSS `--ak-v2-*`, appliqué en
// posant `data-ak-v2-theme="<id>"` sur <html> (blocs CSS dans tailwind.css).
// Les composants ne connaissent jamais le thème : ils lisent les tokens.
//
// Invariants inter-thèmes (sémantique produit, jamais surchargés) :
// couleurs de collecte, catégories (questions + éthogramme), rouge critique,
// palette d'ambiance, focus.
// Élargi pour les thèmes sombre et accessibilité : surfaces, encres, critique
// (la TEINTE du critique peut bouger pour le daltonisme — le rôle « une seule
// couleur = danger » reste), tints doux des collectes (la teinte de base des
// collectes reste invariante) et ombres (thème sombre).
export const THEMEABLE_VARIABLES = freeze([
  "--ak-v2-brand",
  "--ak-v2-brand-hover",
  "--ak-v2-brand-soft",
  "--ak-v2-brand-ink",
  "--ak-v2-highlight",
  "--ak-v2-highlight-hover",
  "--ak-v2-highlight-soft",
  "--ak-v2-highlight-ink",
  "--ak-v2-text",
  "--ak-v2-text-muted",
  "--ak-v2-surface",
  "--ak-v2-surface-subtle",
  "--ak-v2-surface-canvas-tint",
  "--ak-v2-surface-inverse",
  "--ak-v2-surface-inverse-raised",
  "--ak-v2-surface-inverse-ink",
  "--ak-v2-inverse-ink-idle",
  "--ak-v2-inverse-ink-muted",
  "--ak-v2-inverse-ink-subtle",
  "--ak-v2-inverse-ink-disabled",
  "--ak-v2-inverse-surface-hover",
  "--ak-v2-inverse-surface-active",
  "--ak-v2-inverse-rule",
  "--ak-v2-surface-inverse-accent",
  "--ak-v2-focus-inverse",
  "--ak-v2-skeleton-shimmer",
  "--ak-v2-border",
  "--ak-v2-border-strong",
  "--ak-v2-critical",
  "--ak-v2-critical-soft",
  "--ak-v2-collection-observation-soft",
  "--ak-v2-collection-questionnaire-soft",
  "--ak-v2-collection-scan-soft",
  "--ak-v2-collection-fast-evaluation-soft",
  "--ak-v2-dataviz-corail",
  "--ak-v2-dataviz-ambre",
  "--ak-v2-dataviz-turquoise",
  "--ak-v2-dataviz-bleu",
  "--ak-v2-dataviz-violet",
  "--ak-v2-dataviz-rose",
  // Résultats d'évaluation : fixes partout, surchargés par le seul thème daltonien.
  "--ak-v2-outcome-positive",
  "--ak-v2-outcome-positive-ink",
  "--ak-v2-outcome-neutral",
  "--ak-v2-outcome-neutral-ink",
  "--ak-v2-outcome-negative",
  "--ak-v2-outcome-negative-ink",
  "--ak-v2-shadow-surface",
  "--ak-v2-shadow-raised",
  "--ak-v2-shadow-overlay",
  "--ak-v2-shadow-brand-halo",
  "--ak-v2-shadow-rail",
]);

// Les libellés utilisateur des thèmes vivent dans les fichiers de traduction
// (contrat i18n V2 : aucun copy en dur ici) — clés à créer avec le sélecteur.
export const THEME_VARIANTS = freeze({
  // Ordre = ordre d'affichage des sélecteurs. Chaque thème à rail sombre est
  // suivi de son pendant à rail clair (forêt/brume, océan/ardoise,
  // minéral/craie) : sur la grille à deux colonnes, colonne de gauche = rail
  // sombre, colonne de droite = rail clair. Puis savane, nuit, et les deux
  // thèmes d'accessibilité en dernier.
  //
  // Thème de base : les valeurs canoniques des tokens, aucune surcharge.
  foret: freeze({
    overrides: freeze({}),
  }),
  // Thèmes clairs : le rail cesse d'être une bande sombre et devient une
  // surface calme. Deux conséquences portées par les tokens — l'échelle
  // d'encres monte (une encre sombre à 56 % ne vaut que 3,3:1 sur fond clair)
  // et le repère d'état actif quitte le jaune (1,3:1) pour la primaire.
  // « Brume » : vert-gris, pendant clair de « forêt ». La primaire est un vert
  // sauge désaturé : le vert de marque saturé jurait sur des surfaces voilées.
  brume: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#2F6A57",
      "--ak-v2-brand-hover": "#275949",
      "--ak-v2-brand-soft": "#E3EEE9",
      "--ak-v2-text": "#143229",
      "--ak-v2-text-muted": "#4F635A",
      "--ak-v2-surface-subtle": "#F3F7F5",
      "--ak-v2-surface-canvas-tint": "#ECF3EF",
      "--ak-v2-surface-inverse": "#E9EFEB",
      "--ak-v2-surface-inverse-raised": "#DDE6E1",
      "--ak-v2-surface-inverse-ink": "#143229",
      "--ak-v2-inverse-ink-idle": "rgba(20, 50, 41, 0.82)",
      "--ak-v2-inverse-ink-muted": "rgba(20, 50, 41, 0.72)",
      "--ak-v2-inverse-ink-subtle": "rgba(20, 50, 41, 0.68)",
      "--ak-v2-inverse-ink-disabled": "rgba(20, 50, 41, 0.54)",
      "--ak-v2-inverse-surface-hover": "rgba(20, 50, 41, 0.06)",
      "--ak-v2-inverse-surface-active": "rgba(20, 50, 41, 0.1)",
      "--ak-v2-inverse-rule": "rgba(20, 50, 41, 0.16)",
      "--ak-v2-surface-inverse-accent": "#2F6A57",
      "--ak-v2-focus-inverse": "#0A6EBC",
      "--ak-v2-shadow-brand-halo": "0 4px 10px rgba(47, 106, 87, 0.26)",
      "--ak-v2-shadow-rail": "6px 0 18px rgba(20, 50, 41, 0.13)",
    }),
  }),
  ocean: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#0F766E",
      "--ak-v2-brand-hover": "#115E59",
      "--ak-v2-brand-soft": "#E6F2F0",
      "--ak-v2-highlight": "#EFB63F",
      "--ak-v2-highlight-hover": "#DCA22C",
      "--ak-v2-highlight-soft": "#FDF3D7",
      "--ak-v2-highlight-ink": "#14343B",
      "--ak-v2-text": "#14343B",
      "--ak-v2-text-muted": "#4A626A",
      "--ak-v2-surface-subtle": "#F3F7F8",
      "--ak-v2-surface-canvas-tint": "#E7F0F1",
      "--ak-v2-border": "#D8E4E6",
      "--ak-v2-border-strong": "#B9CDD0",
      "--ak-v2-surface-inverse": "#14343B",
      "--ak-v2-surface-inverse-raised": "#254349",
      "--ak-v2-surface-inverse-accent": "#EFB63F",
    }),
  }),
  // « Ardoise claire » : bleu froid, famille « océan » / « daltonien ».
  ardoise: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#0F5F86",
      "--ak-v2-brand-hover": "#0C4E6E",
      "--ak-v2-brand-soft": "#E4EFF5",
      "--ak-v2-highlight-ink": "#1D2A33",
      "--ak-v2-text": "#1D2A33",
      "--ak-v2-text-muted": "#4C5E68",
      "--ak-v2-surface-subtle": "#F4F8FA",
      "--ak-v2-surface-canvas-tint": "#EAF1F5",
      "--ak-v2-border": "#D9E3E9",
      "--ak-v2-border-strong": "#B8C8D2",
      "--ak-v2-surface-inverse": "#E7EDF1",
      "--ak-v2-surface-inverse-raised": "#DAE3E9",
      "--ak-v2-surface-inverse-ink": "#1D2A33",
      "--ak-v2-inverse-ink-idle": "rgba(29, 42, 51, 0.81)",
      "--ak-v2-inverse-ink-muted": "rgba(29, 42, 51, 0.71)",
      "--ak-v2-inverse-ink-subtle": "rgba(29, 42, 51, 0.67)",
      "--ak-v2-inverse-ink-disabled": "rgba(29, 42, 51, 0.53)",
      "--ak-v2-inverse-surface-hover": "rgba(29, 42, 51, 0.06)",
      "--ak-v2-inverse-surface-active": "rgba(29, 42, 51, 0.1)",
      "--ak-v2-inverse-rule": "rgba(29, 42, 51, 0.16)",
      "--ak-v2-surface-inverse-accent": "#0F5F86",
      "--ak-v2-focus-inverse": "#0A6EBC",
      "--ak-v2-shadow-rail": "6px 0 18px rgba(29, 42, 51, 0.13)",
    }),
  }),
  mineral: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#3E6259",
      "--ak-v2-brand-hover": "#33534B",
      "--ak-v2-brand-soft": "#E9EFED",
      "--ak-v2-highlight": "#D9B24A",
      "--ak-v2-highlight-hover": "#C6A03C",
      "--ak-v2-highlight-soft": "#F8F1DC",
      "--ak-v2-highlight-ink": "#24312D",
      "--ak-v2-text": "#24312D",
      "--ak-v2-text-muted": "#5A6763",
      "--ak-v2-surface-subtle": "#F5F7F6",
      "--ak-v2-surface-canvas-tint": "#ECF0EE",
      "--ak-v2-border": "#DDE3E0",
      "--ak-v2-border-strong": "#BEC9C4",
      "--ak-v2-surface-inverse": "#24312D",
      "--ak-v2-surface-inverse-raised": "#333F3C",
      "--ak-v2-surface-inverse-accent": "#D9B24A",
    }),
  }),
  // « Craie » : achromatique — aucune opinion de teinte sur les surfaces.
  // La primaire est un vert graphite : elle garde une trace de la marque sans
  // poser un aplat saturé sur du gris (et tient 5,5:1 sur le rail dense, là où
  // le vert de marque tombait à 3,3:1). Son rail est plus dense que celui des
  // deux autres : il se détache de la page sans recourir à l'ombre.
  craie: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#2F4F45",
      "--ak-v2-brand-hover": "#264138",
      "--ak-v2-brand-soft": "#E6ECE9",
      "--ak-v2-text": "#1C211F",
      "--ak-v2-text-muted": "#565C59",
      "--ak-v2-surface-subtle": "#F6F7F7",
      "--ak-v2-surface-canvas-tint": "#EFF1F0",
      "--ak-v2-border": "#DEE1E0",
      "--ak-v2-border-strong": "#C2C7C5",
      "--ak-v2-surface-inverse": "#D3D7D6",
      "--ak-v2-surface-inverse-raised": "#C7CCCA",
      "--ak-v2-surface-inverse-ink": "#1C211F",
      "--ak-v2-inverse-ink-idle": "rgba(28, 33, 31, 0.82)",
      "--ak-v2-inverse-ink-muted": "rgba(28, 33, 31, 0.71)",
      "--ak-v2-inverse-ink-subtle": "rgba(28, 33, 31, 0.67)",
      "--ak-v2-inverse-ink-disabled": "rgba(28, 33, 31, 0.53)",
      "--ak-v2-inverse-surface-hover": "rgba(28, 33, 31, 0.06)",
      "--ak-v2-inverse-surface-active": "rgba(28, 33, 31, 0.1)",
      "--ak-v2-inverse-rule": "rgba(28, 33, 31, 0.16)",
      "--ak-v2-surface-inverse-accent": "#2F4F45",
      "--ak-v2-focus-inverse": "#0A6EBC",
      "--ak-v2-shadow-brand-halo": "0 4px 10px rgba(47, 79, 69, 0.24)",
      "--ak-v2-shadow-rail": "6px 0 18px rgba(28, 33, 31, 0.1)",
    }),
  }),
  savane: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#815B21",
      "--ak-v2-brand-hover": "#6C4B1B",
      "--ak-v2-brand-soft": "#F4EBDD",
      "--ak-v2-highlight": "#E8A13C",
      "--ak-v2-highlight-hover": "#D18F2C",
      "--ak-v2-highlight-soft": "#FBEFDB",
      "--ak-v2-highlight-ink": "#3A3126",
      "--ak-v2-text": "#3A3126",
      "--ak-v2-text-muted": "#6B5F4E",
      "--ak-v2-surface-subtle": "#F7F4EE",
      "--ak-v2-surface-canvas-tint": "#F0E9DC",
      "--ak-v2-border": "#E3DCCE",
      "--ak-v2-border-strong": "#C9BFA9",
      "--ak-v2-surface-inverse": "#3A3126",
      "--ak-v2-surface-inverse-raised": "#483F35",
      "--ak-v2-surface-inverse-accent": "#E8A13C",
    }),
  }),
  // Thème sombre « minéral » : surfaces gris pierre neutres (pas de fond
  // teinté vert — le vert reste réservé à l'action), marque éclaircie pour le
  // contraste, ombres renforcées.
  nuit: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#4CC38A",
      "--ak-v2-brand-hover": "#63D49C",
      "--ak-v2-brand-soft": "#223029",
      "--ak-v2-brand-ink": "#0F1211",
      "--ak-v2-highlight": "#F3C94E",
      "--ak-v2-highlight-hover": "#E4B92E",
      "--ak-v2-highlight-soft": "#2E2A1C",
      "--ak-v2-highlight-ink": "#1A1608",
      "--ak-v2-text": "#ECF0EE",
      "--ak-v2-text-muted": "#A6B0AC",
      "--ak-v2-surface": "#1B1F1E",
      "--ak-v2-surface-subtle": "#121514",
      "--ak-v2-surface-canvas-tint": "#0E100F",
      "--ak-v2-border": "#2E3432",
      "--ak-v2-border-strong": "#48504D",
      "--ak-v2-surface-inverse": "#0B0E0D",
      "--ak-v2-surface-inverse-raised": "#212423",
      "--ak-v2-inverse-ink-idle": "rgba(236, 240, 238, 0.74)",
      "--ak-v2-inverse-ink-muted": "rgba(236, 240, 238, 0.64)",
      "--ak-v2-inverse-ink-subtle": "rgba(236, 240, 238, 0.56)",
      "--ak-v2-inverse-ink-disabled": "rgba(236, 240, 238, 0.52)",
      "--ak-v2-inverse-surface-hover": "rgba(236, 240, 238, 0.09)",
      "--ak-v2-inverse-surface-active": "rgba(236, 240, 238, 0.15)",
      "--ak-v2-inverse-rule": "rgba(236, 240, 238, 0.14)",
      "--ak-v2-surface-inverse-ink": "#ECF0EE",
      "--ak-v2-skeleton-shimmer": "rgba(255, 255, 255, 0.07)",
      "--ak-v2-critical": "#E37A72",
      "--ak-v2-critical-soft": "#332120",
      "--ak-v2-collection-observation-soft": "#1E2A25",
      "--ak-v2-collection-questionnaire-soft": "#1C242C",
      "--ak-v2-collection-scan-soft": "#251F2A",
      "--ak-v2-collection-fast-evaluation-soft": "#2A2318",
      "--ak-v2-shadow-surface": "0 1px 2px rgba(0, 0, 0, 0.5)",
      "--ak-v2-shadow-raised": "0 2px 4px rgba(0, 0, 0, 0.4), 0 10px 24px rgba(0, 0, 0, 0.5)",
      "--ak-v2-shadow-overlay": "0 12px 28px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.45)",
      "--ak-v2-shadow-brand-halo": "0 4px 10px rgba(76, 195, 138, 0.25)",
      "--ak-v2-shadow-rail": "6px 0 18px rgba(0, 0, 0, 0.5)",
    }),
  }),
  // Accessibilité malvoyance : contrastes maximaux (cibles AAA sur le texte),
  // fonds tonals supprimés, bordures noires. Les surfaces étant toutes
  // blanches, les ombres diffuses ne délimitent plus rien : elles deviennent
  // des anneaux pleins (contours nets sur cards, panneaux et overlays).
  contraste: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#005A2E",
      "--ak-v2-brand-hover": "#003D1F",
      "--ak-v2-brand-soft": "#D6EFE2",
      "--ak-v2-highlight": "#FFD500",
      "--ak-v2-highlight-hover": "#E5BF00",
      "--ak-v2-highlight-soft": "#FFF3B8",
      "--ak-v2-highlight-ink": "#000000",
      "--ak-v2-text": "#000000",
      "--ak-v2-text-muted": "#1F2621",
      "--ak-v2-surface-subtle": "#FFFFFF",
      "--ak-v2-surface-canvas-tint": "#FFFFFF",
      "--ak-v2-border": "#1F2621",
      "--ak-v2-border-strong": "#000000",
      "--ak-v2-surface-inverse": "#000000",
      "--ak-v2-surface-inverse-raised": "#000000",
      "--ak-v2-inverse-ink-idle": "rgba(255, 255, 255, 1)",
      "--ak-v2-inverse-ink-muted": "rgba(255, 255, 255, 0.85)",
      "--ak-v2-inverse-ink-subtle": "rgba(255, 255, 255, 0.78)",
      "--ak-v2-inverse-ink-disabled": "rgba(255, 255, 255, 0.62)",
      "--ak-v2-inverse-surface-hover": "rgba(255, 255, 255, 0.14)",
      "--ak-v2-inverse-surface-active": "rgba(255, 255, 255, 0.24)",
      "--ak-v2-inverse-rule": "rgba(255, 255, 255, 0.28)",
      "--ak-v2-surface-inverse-accent": "#FFD500",
      "--ak-v2-focus-inverse": "#FFFFFF",
      "--ak-v2-critical": "#8F1D16",
      "--ak-v2-critical-soft": "#FFE4E1",
      "--ak-v2-shadow-surface": "0 0 0 1px #000000",
      "--ak-v2-shadow-raised": "0 0 0 2px #000000",
      "--ak-v2-shadow-overlay": "0 0 0 2px #000000",
      "--ak-v2-shadow-brand-halo": "0 0 0 2px #003D1F",
      "--ak-v2-shadow-rail": "6px 0 0 -5px #000000",
    }),
  }),
  // Accessibilité daltonisme (deutéranopie/protanopie) : axes bleu/vermillon
  // (palette Okabe-Ito). La teinte « danger » quitte le rouge pur ; les
  // catégories imposées restent inchangées mais la charte fait porter le sens
  // par icône + libellé, jamais par la couleur seule.
  daltonien: freeze({
    overrides: freeze({
      "--ak-v2-brand": "#0072B2",
      "--ak-v2-brand-hover": "#005A8E",
      "--ak-v2-brand-soft": "#E3F0F8",
      "--ak-v2-highlight": "#E69F00",
      "--ak-v2-highlight-hover": "#CC8D00",
      "--ak-v2-highlight-soft": "#FBEFD5",
      "--ak-v2-highlight-ink": "#1F2621",
      "--ak-v2-text": "#1D2A33",
      "--ak-v2-text-muted": "#4C5E68",
      "--ak-v2-surface-subtle": "#F4F7F9",
      "--ak-v2-surface-canvas-tint": "#EAF0F4",
      "--ak-v2-border": "#D6DFE4",
      "--ak-v2-border-strong": "#B4C2CA",
      "--ak-v2-surface-inverse": "#1D2A33",
      "--ak-v2-surface-inverse-raised": "#2D3941",
      "--ak-v2-surface-inverse-accent": "#E69F00",
      "--ak-v2-critical": "#A84A00",
      "--ak-v2-critical-soft": "#FBE3D6",
      "--ak-v2-dataviz-corail": "#D55E00",
      "--ak-v2-dataviz-ambre": "#E69F00",
      "--ak-v2-dataviz-turquoise": "#009E73",
      "--ak-v2-dataviz-bleu": "#0072B2",
      "--ak-v2-dataviz-violet": "#CC79A7",
      "--ak-v2-dataviz-rose": "#56B4E9",
      "--ak-v2-outcome-positive": "#0072B2",
      "--ak-v2-outcome-neutral": "#E69F00",
      "--ak-v2-outcome-neutral-ink": "#1F2621",
      "--ak-v2-outcome-negative": "#A84A00",
    }),
  }),
});

// « Forêt » porte les valeurs canoniques des tokens : aucun bloc CSS, aucun
// attribut sur <html>.
export const BASE_THEME_VARIANT = "foret";

// Thème appliqué tant que personne n'a choisi sur cet appareil (océan depuis
// le 05/10/2026). Un choix enregistré, forêt compris, l'emporte toujours.
export const DEFAULT_THEME_VARIANT = "ocean";

export const THEME_VARIANT_IDS = freeze(Object.keys(THEME_VARIANTS));

// Applique un thème en posant l'attribut consommé par les blocs CSS.
// Le thème de base retire l'attribut (aucune surcharge active).
export const applyThemeVariant = (variantId, root = typeof document !== "undefined" ? document.documentElement : null) => {
  if (!root) return DEFAULT_THEME_VARIANT;
  const resolved = THEME_VARIANTS[variantId] ? variantId : DEFAULT_THEME_VARIANT;
  if (resolved === BASE_THEME_VARIANT) {
    root.removeAttribute("data-ak-v2-theme");
  } else {
    root.setAttribute("data-ak-v2-theme", resolved);
  }
  return resolved;
};

// Échantillons résolus d'un thème (prévisualisation dans le sélecteur) :
// dérivés de THEME_VARIANTS + valeurs canoniques, jamais dupliqués ailleurs.
export const themeVariantSwatches = (variantId) => {
  const overrides = (THEME_VARIANTS[variantId] || THEME_VARIANTS[DEFAULT_THEME_VARIANT]).overrides;
  const value = (variable) => overrides[variable] || THEME_A_CSS_VARIABLES[variable];
  return freeze({
    brand: value("--ak-v2-brand"),
    brandSoft: value("--ak-v2-brand-soft"),
    border: value("--ak-v2-border"),
    highlight: value("--ak-v2-highlight"),
    // Le rail n'est pas « la couleur du texte » : clair sur brume / ardoise /
    // craie, presque noir sur nuit. La miniature lit les vrais tokens du rail.
    rail: value("--ak-v2-surface-inverse"),
    railAccent: value("--ak-v2-surface-inverse-accent"),
    railInk: value("--ak-v2-surface-inverse-ink"),
    surface: value("--ak-v2-surface-subtle"),
    surfaceCard: value("--ak-v2-surface"),
    text: value("--ak-v2-text"),
    textMuted: value("--ak-v2-text-muted"),
  });
};

// Thème réellement peint : sans attribut, ce sont les tokens canoniques.
export const currentThemeVariant = (root = typeof document !== "undefined" ? document.documentElement : null) => {
  const value = root ? root.getAttribute("data-ak-v2-theme") : null;
  return THEME_VARIANTS[value] ? value : BASE_THEME_VARIANT;
};
